import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  Checkbox,
  Container,
  TextField,
} from '@mui/material';
import { Button } from '../../../Components/MUIs';
import FilterBar from '../../../Components/Ui/FilterBar';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { setDepCd } from '../../../Reducer/SearchReducer';
import SearchBar from '../../../Components/Ui/SearchBar';

export default function M_EIC_BASIC(props) {
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState('');
  const filterState = props.filterState ?? {};

  const depOptions = (props.depList ?? []).map((d) => ({
    value: d.value ?? '',
    label: d.name ?? d.label ?? '',
  }));
  const filterItems = useMemo(
    () => [
      { key: 'dep', label: '부서', value: filterState.depName ?? '전체' },

    ],
    [
      filterState.depName,
    ]
  );

  const filterOptionsByKey = useMemo(
    () => ({
      dep: depOptions,
    }),
    [depOptions]
  );

  const HandleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const filteredEmployeeList = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    const selectedDepCd = props.filterState?.depCd ?? '';

    return (props.employeeList ?? []).filter((employee) => {
      const employeeDepCd = employee?.depCd ?? '';
      const isDepMatched = !selectedDepCd || employeeDepCd === selectedDepCd;
      if (!isDepMatched) return false;

      if (!keyword) return true;

      const employeeName = (employee?.name ?? '').toLowerCase();
      const employeeId = String(employee?.id ?? '').toLowerCase();
      return employeeName.includes(keyword) || employeeId.includes(keyword);
    });
  }, [props.employeeList, props.filterState?.depCd, searchKeyword]);

  const HandleFilterSelect = (key, option) => {
    if (key === 'dep') {
      dispatch(setDepCd(option.value));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SearchBar
        value={searchKeyword}
        onChange={HandleSearchChange}
        placeholder="이름 또는 ID 검색"
      />

      <FilterBar
        items={filterItems}
        optionsByKey={filterOptionsByKey}
        onSelect={HandleFilterSelect}
      />

      <Container
        sx={{
          py: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >

        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          {/* 왼쪽: 근로자 목록 */}
          <Box sx={{ width: '40%', borderRight: '1px solid #e0e0e0', pr: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              근로자 목록
            </Typography>
            <Box
              sx={{
                height: 'calc(100% - 40px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {filteredEmployeeList.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {searchKeyword.trim()
                    ? '검색 결과가 없습니다.'
                    : '등록된 근로자가 없습니다.'}
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {filteredEmployeeList.map((employee, index) => (
                    <React.Fragment key={employee.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={props.selectedEmployee?.id === employee.id}
                          onClick={() => props.onEmployeeSelect(employee)}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd',
                              '&:hover': {
                                backgroundColor: '#bbdefb',
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={employee.name}
                            secondary={employee.id}
                            primaryTypographyProps={{
                              fontSize: '14px',
                              fontWeight:
                                props.selectedEmployee?.id === employee.id
                                  ? 'bold'
                                  : 'normal',
                            }}
                            secondaryTypographyProps={{
                              fontSize: '12px',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < filteredEmployeeList.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          {/* 오른쪽: 보호구 등록 영역 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {props.selectedEmployee ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {props.selectedEmployee.name}님의 보호구
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    지급하신 보호구를 선택하세요
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    mb: 2,
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  {props.equipList.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      등록된 보호구가 없습니다.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {props.equipList.map((equip) => {
                        const equipId = equip.erCd;
                        const isSelected =
                          equipId && props.selectedEquips.includes(equipId);

                        return (
                          <Card
                            key={equipId}
                            sx={{
                              border: isSelected
                                ? '2px solid #1976d2'
                                : '1px solid #e0e0e0',
                              borderRadius: 2,
                            }}
                          >
                            <CardContent
                              sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                {/* 체크박스와 보호구 이름을 같은 줄에 배치 */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => props.onEquipToggle(equipId)}
                                    sx={{ p: 0.5 }}
                                  />
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'medium', flex: 1 }}
                                  >
                                    {equip.equipNm}
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        fontSize: '0.875rem',
                                        color: 'text.secondary',
                                        fontWeight: 'normal',
                                      }}
                                    >
                                      (재고: {equip.stockQty})
                                    </Box>
                                  </Typography>
                                </Box>
                                {/* 입력 필드는 선택 시에만 아래에 표시 */}
                                {isSelected && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    <TextField
                                      label="수량"
                                      type="number"
                                      size="small"
                                      value={
                                        props.equipInputs[equipId]?.qty || ''
                                      }
                                      onChange={(e) =>
                                        props.onInputChange(
                                          equipId,
                                          'qty',
                                          e.target.value
                                        )
                                      }
                                      inputProps={{ min: 0 }}
                                    />
                                    <TextField
                                      label="비고"
                                      size="small"
                                      multiline
                                      rows={2}
                                      value={
                                        props.equipInputs[equipId]?.remark || ''
                                      }
                                      onChange={(e) =>
                                        props.onInputChange(
                                          equipId,
                                          'remark',
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    name="저장"
                    sx={{
                      flex: 1,
                      height: 40,
                      borderRadius: '5px',
                      backgroundColor: '#57B84D',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#4a9e42',
                      },
                    }}
                    onClick={props.onSave}
                    disabled={props.selectedEquips.length === 0}
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  왼쪽에서 근로자를 선택하세요
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
}
